import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useAuth } from "../../../hooks/useAuth";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";
import { adminApi } from "../../../lib/admin";
import type { PropertyPredicateDto } from "../../../types/gigTypes";
import { endOfLocalDay, parseLocalDateInput, startOfLocalDay, toIsoTimestamp } from "../../../utils/DateUtils";
import { hasRole } from "../../../utils/AuthUtils";
import { apiErrorToMessage } from "../../../utils/ApiUtils";
import AdminPaymentsTable from "./AdminPaymentsTable";

const DEFAULT_PAGE_SIZE = 10;

const AdminPaymentsPanel = () => {
  const { user } = useAuth();
  const [search, setSearch] = useState("");
  const [paymentType, setPaymentType] = useState<"GIG" | "BUSINESS_LISTING_FEE">("GIG");
  const [status, setStatus] = useState("");
  const [createdFromDate, setCreatedFromDate] = useState("");
  const [createdToDate, setCreatedToDate] = useState("");
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [page, setPage] = useState(0);
  const debouncedSearch = useDebouncedValue(search, 300);
  const canWrite = hasRole(user?.roles, "ADMIN_WRITE");

  const predicates: PropertyPredicateDto[] = [];
  if (debouncedSearch.trim().length > 0) {
    predicates.push({ propertyName: "search", propertyValue: debouncedSearch.trim(), operation: "CONTAINS" });
  }
  predicates.push({ propertyName: "paymentType", propertyValue: paymentType, operation: "EQUALS" });
  if (status.trim().length > 0) {
    predicates.push({ propertyName: "status", propertyValue: status.trim(), operation: "EQUALS" });
  }
  const createdFrom = parseLocalDateInput(createdFromDate);
  const createdTo = parseLocalDateInput(createdToDate);
  if (createdFrom) {
    predicates.push({ propertyName: "createdAt", propertyValue: toIsoTimestamp(startOfLocalDay(createdFrom)), operation: "TIMESTAMP_FROM" });
  }
  if (createdTo) {
    predicates.push({ propertyName: "createdAt", propertyValue: toIsoTimestamp(endOfLocalDay(createdTo)), operation: "TIMESTAMP_TO" });
  }

  const paymentsQuery = useQuery({
    queryKey: ["admin-payments", { page, pageSize, debouncedSearch, paymentType, status, createdFromDate, createdToDate }],
    queryFn: () =>
      adminApi.listPayments({
        predicates,
        page: {
          number: page,
          size: pageSize,
          numberOfElements: null,
          hasNext: false,
          hasPrevious: false,
          first: false,
          last: false,
          sortBy: [{ direction: "DESC", propertyName: "createdAt" }],
        },
      }),
    placeholderData: (previousData) => previousData,
  });

  const error = paymentsQuery.error ? apiErrorToMessage(paymentsQuery.error, "Failed to load payments.") : null;

  return (
    <div>
      {error ? <p className="mb-4 text-sm font-medium text-red-600">{error}</p> : null}
      <AdminPaymentsTable
        payments={paymentsQuery.data?.payments ?? []}
        isLoading={paymentsQuery.isLoading}
        canWrite={canWrite}
        search={search}
        paymentType={paymentType}
        status={status}
        createdFromDate={createdFromDate}
        createdToDate={createdToDate}
        pageSize={pageSize}
        page={page}
        resultsCount={paymentsQuery.data?.page.numberOfElements ?? 0}
        hasNextPage={paymentsQuery.data?.page.hasNext ?? false}
        hasPreviousPage={paymentsQuery.data?.page.hasPrevious ?? false}
        onSearchChange={(value) => {
          setSearch(value);
          setPage(0);
        }}
        onPaymentTypeChange={(value) => {
          setPaymentType(value);
          setStatus("");
          setPage(0);
        }}
        onStatusChange={(value) => {
          setStatus(value);
          setPage(0);
        }}
        onCreatedFromDateChange={(value) => {
          setCreatedFromDate(value);
          setPage(0);
        }}
        onCreatedToDateChange={(value) => {
          setCreatedToDate(value);
          setPage(0);
        }}
        onPageSizeChange={(value) => {
          setPageSize(value);
          setPage(0);
        }}
        onPageChange={setPage}
      />
    </div>
  );
};

export default AdminPaymentsPanel;
